import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyUri } from "../key";

const VendorLogin = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    mobile: '',
    email: '',
    password: '',
    otp: '',
    step: 'login',
    loginType: 'mobile'
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const { mobile, email, password, otp, step, loginType } = formData;

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const isPhone = /^\d{10}$/.test(email.trim());

  // Send OTP
  const sendOTP = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const payload =
        loginType === 'mobile'
          ? { phone_number: mobile }
          : isPhone
          ? { phone_number: email.trim() }
          : { email: email.trim() };

      const res = await fetch(KeyUri.BACKEND_URI + '/vendor/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        setFormData({ ...formData, step: 'otp' });
        const destination =
          loginType === 'mobile' ? `+91 ${mobile}` : email;
        alert(`OTP sent to ${destination}!`);
      } else {
        setError(data.message || 'Failed to send OTP');
      }
    } catch {
      setError('Server error. Please try again.');
    }
    setLoading(false);
  };

  // Verify OTP
  const verifyOTP = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const payload =
        loginType === 'mobile'
          ? { phone_number: mobile, otp }
          : isPhone
          ? { phone_number: email.trim(), otp }
          : { email: email.trim(), otp };

      const res = await fetch(KeyUri.BACKEND_URI + '/vendor/verify-otp-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem('vendorToken', data.token);
        onLoginSuccess(data.vendor);
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch {
      setError('Server error. Please try again.');
    }
    setLoading(false);
  };

  // Password Login (email tab only)
  const passwordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const payload = isPhone
        ? { phone_number: email.trim(), password }
        : { email: email.trim(), password };

      const res = await fetch(KeyUri.BACKEND_URI + '/vendor/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem('vendorToken', data.token);
        onLoginSuccess(data.vendor);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch {
      setError('Server error. Please try again.');
    }
    setLoading(false);
  };

  const switchTab = (type) => {
    setFormData({
      ...formData,
      loginType: type,
      mobile: '',
      email: '',
      password: '',
      otp: '',
      step: 'login'
    });
    setError(null);
  };

  return (
    <div className="max-w-md mx-auto mt-24 lg:mt-40 mb-4 p-8 bg-white rounded-lg shadow-xl">
      <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">
        {step === 'otp' ? 'Enter OTP' : 'Vendor Login'}
      </h2>

      <p className="text-center text-gray-600 text-sm mb-6">
        Don't have an account?{' '}
        <Link
          to="/vendor/register"
          className="text-blue-600 hover:underline font-medium"
        >
          Register
        </Link>
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {step === 'login' ? (
        <>
          {/* Tab Toggle */}
          <div className="mb-6 p-1 bg-gray-100 rounded-lg flex border-2 border-gray-200">
            <button
              type="button"
              onClick={() => switchTab('mobile')}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200 ${
                loginType === 'mobile'
                  ? 'bg-green-500 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Mobile
            </button>
            <button
              type="button"
              onClick={() => switchTab('email')}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-all duration-200 ${
                loginType === 'email'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Email 
            </button>
          </div>

          {/* ── MOBILE TAB: OTP only ── */}
          {loginType === 'mobile' && (
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="9123456789"
                  value={mobile}
                  onChange={onChange}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={loading}
                />
              </div>

              <button
                type="button"
                onClick={sendOTP}
                disabled={loading || mobile.length !== 10}
                className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition duration-200"
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>

              <p className="text-xs text-gray-500 text-center">
                OTP will be sent to +91 {mobile || 'your mobile number'}
              </p>
            </div>
          )}

          {/* ── EMAIL/PHONE TAB: Password + OTP both ── */}
          {loginType === 'email' && (
            <>
              {/* Password Login */}
              <form onSubmit={passwordLogin} className="space-y-4">
                <div>
                  <label className="block mb-2 font-medium text-gray-700">
                    Email or Phone Number
                  </label>
                  <input
                    type="text"
                    name="email"
                    placeholder="your@email.com or 9123456789"
                    value={email}
                    onChange={onChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block mb-2 font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={onChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition duration-200"
                >
                  {loading ? 'Logging in...' : 'Login with Password'}
                </button>
              </form>

              {/* OTP Option */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center mb-3">
                  Forgot password? Login with OTP instead.
                </p>
                <button
                  type="button"
                  onClick={sendOTP}
                  disabled={loading || !email}
                  className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition duration-200"
                >
                  {loading ? 'Sending...' : 'Login with OTP'}
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  OTP will be sent to {email || 'your email or phone'}
                </p>
              </div>
            </>
          )}
        </>
      ) : (
        // OTP Verification Screen
        <>
          <form onSubmit={verifyOTP} className="space-y-4">
            <div>
              <label className="block mb-2 font-medium text-gray-700">
                {loginType === 'mobile' ? 'Mobile (OTP sent)' : 'Email / Phone (OTP sent)'}
              </label>
              <input
                type="text"
                value={loginType === 'mobile' ? mobile : email}
                readOnly
                className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Enter 6-digit OTP
              </label>
              <input
                type="text"
                name="otp"
                maxLength="6"
                placeholder="123456"
                value={otp}
                onChange={onChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-center text-2xl font-mono tracking-wider"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition duration-200"
            >
              {loading ? 'Verifying...' : 'Verify OTP & Login'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() =>
                setFormData({ ...formData, step: 'login', otp: '' })
              }
              className="text-blue-600 hover:underline text-sm font-medium"
            >
              ← Back to Login
            </button>
          </div>
        </>
      )}

      {/* Forgot Password */}
      <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
        <Link
          to="/vendor/reset-password"
          className="block w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg text-center transition-all duration-200"
        >
          🔑 Forgot Password?
        </Link>
        <p className="text-xs text-gray-500 text-center">
          Reset your password via OTP
        </p>
      </div>
    </div>
  );
};

export default VendorLogin;